import { DateField } from '@internal/ui-kit/components/admin'
import { Badge } from '@internal/ui-kit/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@internal/ui-kit/components/ui/table'
import { fromApiUtc } from '@internal/ui-kit/lib/fromApiUtc'
import { percentDisplayValue } from '@internal/ui-kit/lib/percentField'
import { AddPointsTransactionDrawer } from './AddPointsTransactionDrawer'
import { EditReferralCodeDrawer } from './EditReferralCodeDrawer'
import { usePointsAccount, usePointsTransactions, useReferralDiscountCode } from './useUserProfileData'

/** `getPointsTransactions` — `table3`. */
function PointsTransactionsTable({
  transactions,
  isPending,
}: {
  transactions: ReturnType<typeof usePointsTransactions>['transactions']
  isPending: boolean
}) {
  if (isPending) return <p className="text-muted-foreground text-sm">Loading transactions…</p>
  if (transactions.length === 0) return <p className="text-muted-foreground text-sm">No points transactions.</p>

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Title</TableHead>
          <TableHead>Subtitle</TableHead>
          <TableHead>Amount</TableHead>
          <TableHead>Reference type</TableHead>
          <TableHead>Created at</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {transactions.map((transaction) => (
          <TableRow key={transaction.transaction_id}>
            <TableCell>{transaction.title ?? '—'}</TableCell>
            <TableCell>{transaction.subtitle ?? '—'}</TableCell>
            <TableCell>{transaction.amount ?? '—'}</TableCell>
            <TableCell>{transaction.reference_type ?? '—'}</TableCell>
            <TableCell>
              {transaction.created_at ? (
                <DateField record={transaction} source="created_at" showTime transform={fromApiUtc} />
              ) : (
                '—'
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/**
 * The combined page's "Loyalty" tab (HATCH-101 round 4 — narrowed to points,
 * points transactions, and the referral code per ticket feedback; inventory
 * moved to its own tab, see `InventoryTab.tsx`). Adds `createPointsTransaction`
 * (points can be added or subtracted, a negative `amount`) and
 * `patchReferralDiscountCode` (discount-amount/percent fields, "AS IN
 * RETOOL"), both deferred in the original PR. Round 5 feedback: the referral
 * code's own configuration (amounts/percent/multi-use), not just its `code`,
 * is now also shown in read mode, next to the edit action.
 */
export function LoyaltyTab({ userId }: { userId: string }) {
  const { pointsAccount, isPending: accountPending, refetch: refetchAccount } = usePointsAccount(userId)
  const { transactions, isPending: transactionsPending, refetch: refetchTransactions } = usePointsTransactions(userId)
  const { referralDiscountCode, isPending: referralPending, refetch: refetchReferral } = useReferralDiscountCode(userId)

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium">Points</h4>
          <p className="text-2xl font-semibold">
            {accountPending ? '…' : (pointsAccount?.balance ?? 0)}{' '}
            <span className="text-muted-foreground text-base font-normal">Points</span>
          </p>
        </div>
        <AddPointsTransactionDrawer
          userId={userId}
          accountId={pointsAccount?.account_id}
          onCreated={() => {
            refetchAccount()
            refetchTransactions()
          }}
        />
      </div>

      <section className="flex flex-col gap-3">
        <h4 className="font-medium">Points transactions</h4>
        <PointsTransactionsTable transactions={transactions} isPending={transactionsPending} />
      </section>

      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h4 className="font-medium">Referral Discount Code</h4>
          <p className="text-lg">{referralPending ? '…' : (referralDiscountCode?.code ?? '—')}</p>
          {referralDiscountCode && (
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="outline">Referral discount: {referralDiscountCode.referral_discount_amount ?? '—'}</Badge>
              <Badge variant="outline">Referrer discount: {referralDiscountCode.referrer_discount_amount ?? '—'}</Badge>
              <Badge variant="outline">
                Referrer percent: {percentDisplayValue(referralDiscountCode.referrer_discount_percent) || '—'}%
              </Badge>
              <Badge variant="outline">{referralDiscountCode.is_multi_use ? 'Multi-use' : 'Single-use'}</Badge>
            </div>
          )}
        </div>
        {referralDiscountCode && (
          <EditReferralCodeDrawer referralDiscountCode={referralDiscountCode} onUpdated={refetchReferral} />
        )}
      </div>
    </div>
  )
}
